import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Users, CheckCircle, Clock } from "lucide-react";
import { api } from "../api";
import { useAuth } from "../auth";
import { mediaUrl } from "../config";
import { Spinner, Empty } from "../components/cards";

const JoinGroup = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const { user, ready } = useAuth();
  const [info, setInfo] = useState(undefined);
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/invites/${code}`).then(setInfo).catch(() => setInfo(null));
  }, [code]);

  const join = async () => {
    setState("joining");
    setError("");
    try {
      const r = await api.post(`/invites/${code}/join`);
      const cid = r.conversationId || r.id;
      if (cid && (r.joined !== false)) {
        navigate(`/app/messages/${cid}`);
      } else {
        setState("requested");
      }
    } catch (e) {
      if (e.message.toLowerCase().includes("approval") || e.message.toLowerCase().includes("request")) setState("requested");
      else {
        setError(e.message);
        setState("idle");
      }
    }
  };

  if (info === undefined) return <Spinner />;
  if (info === null) {
    return (
      <div className="mx-auto max-w-md py-24" data-testid="join-invalid">
        <Empty>This group link is invalid or has been reset. Ask the group admin for a fresh link.</Empty>
      </div>
    );
  }

  if (ready && !user) {
    return (
      <div className="mx-auto max-w-md py-24 text-center" data-testid="join-signin">
        <Empty>
          <p>Sign in to join this group.</p>
          <button onClick={() => navigate("/app/login")} className="mx-auto mt-4 block rounded-full bg-crimson px-6 py-2.5 font-heading text-xs font-bold text-white hover:bg-crimson-hover" data-testid="join-signin-button">
            Sign in
          </button>
        </Empty>
      </div>
    );
  }

  const needsApproval = info.requireApproval && !info.isPublic;

  return (
    <div className="mx-auto max-w-md py-20" data-testid="join-group-page">
      <div className="rounded-3xl border border-white/10 bg-surface/80 p-8 text-center">
        <div className="mx-auto flex h-20 w-20 items-center justify-center overflow-hidden rounded-3xl bg-crimson/20 text-crimson">
          {mediaUrl(info.avatar) ? (
            <img src={mediaUrl(info.avatar)} alt="" className="h-full w-full object-cover" />
          ) : (
            <Users size={30} />
          )}
        </div>
        <h1 className="mt-5 font-heading text-2xl font-black tracking-tight text-white" data-testid="join-group-name">
          {info.name || info.title || "Group chat"}
        </h1>
        <p className="mt-1 text-sm text-slate-500">{info.memberCount ?? ""} members</p>

        {state === "requested" ? (
          <div className="mt-6" data-testid="join-requested">
            <Clock size={28} className="mx-auto text-amber-400" />
            <p className="mt-3 font-heading text-sm font-semibold text-white">Request sent</p>
            <p className="mt-1 text-sm text-slate-400">An admin needs to approve you before you join. You'll see the group in your chats once you're in.</p>
          </div>
        ) : (
          <button onClick={join} disabled={state === "joining"} className="mt-6 w-full rounded-full bg-crimson py-3 font-heading text-sm font-bold text-white transition-colors hover:bg-crimson-hover disabled:opacity-50" data-testid="join-group-button">
            {state === "joining" ? "Joining…" : needsApproval ? "Request to join" : "Join group"}
          </button>
        )}
        {needsApproval && state !== "requested" && (
          <p className="mt-3 text-xs text-slate-500">This group requires admin approval for new members.</p>
        )}
        {error && <p className="mt-3 text-sm text-crimson">{error}</p>}
      </div>
    </div>
  );
};

export default JoinGroup;
